/**
 * Fractalot State Store
 *
 * Central state management using a simple pub/sub pattern.
 * All app state lives here. Components subscribe to changes.
 */

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
}

export interface Parcel {
  id: string;
  userId: string;
  createdAt: string;
  name?: string;
  thumbnail?: string;

  // Position
  re: number;
  im: number;
  zoom: number;

  // Rendering config (for exact visual replication)
  iterations: number;
  colorScheme: string;
  reverseColors: boolean;
  smoothColoring: boolean;
  paletteMinIter: number;
  paletteMaxIter: number;
  scaleWithIterations: boolean;
  shiftHueAmount: number;
  saturateAmount: number;
  lightenAmount: number;
  colorSpace: number;

  // Claim fields
  claimed: boolean;
  claimedAt?: string;
  displayName?: string;
  stripePaymentId?: string;
  thumbnailUrl?: string;
}

export interface Order {
  id: string;
  userId: string;
  parcelId: string;
  productType: string;
  variantId: number;
  color?: string;
  size?: string;
  imageUrl: string;
  stripePaymentId: string;
  printfulOrderId?: number;
  status: string;
  errorMessage?: string;
  createdAt: string;
}

export interface MapPosition {
  re: number;
  im: number;
  zoom: number;
}

export interface AppState {
  // Navigation
  currentView: "explorer" | "collection" | "gallery";

  // Authentication
  isAuthenticated: boolean;
  user: User | null;

  // Map position (synced with MandelbrotMap)
  mapPosition: MapPosition;

  // Parcels
  ownedParcels: Parcel[];

  // Gallery (public claimed parcels)
  galleryParcels: Parcel[];

  // Orders (merch)
  orders: Order[];

  // Claim flow
  claimingParcelId: string | null;
}

type Listener = (state: AppState) => void;
type Selector<T> = (state: AppState) => T;

// ============================================================================
// Store Implementation
// ============================================================================

class Store {
  private state: AppState;
  private listeners: Set<Listener> = new Set();

  constructor(initialState: AppState) {
    this.state = initialState;
  }

  /**
   * Get current state (read-only snapshot)
   */
  getState(): Readonly<AppState> {
    return this.state;
  }

  /**
   * Update state with partial changes
   */
  setState(partial: Partial<AppState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  /**
   * Subscribe to all state changes
   * Returns unsubscribe function
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Call immediately with current state
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to specific state slice (only notifies when slice changes)
   * Returns unsubscribe function
   */
  select<T>(selector: Selector<T>, listener: (value: T) => void): () => void {
    let previousValue = selector(this.state);
    listener(previousValue); // Call immediately

    return this.subscribe((state) => {
      const currentValue = selector(state);
      if (currentValue !== previousValue) {
        previousValue = currentValue;
        listener(currentValue);
      }
    });
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }
}

// ============================================================================
// Store Instance & Initial State
// ============================================================================

const initialState: AppState = {
  currentView: "explorer",
  isAuthenticated: false,
  user: null,
  mapPosition: { re: -0.5, im: 0, zoom: 3 },
  ownedParcels: [],
  galleryParcels: [],
  orders: [],
  claimingParcelId: null,
};

export const store = new Store(initialState);

// ============================================================================
// Convenience Selectors
// ============================================================================

export const selectors = {
  currentView: (state: AppState) => state.currentView,
  isAuthenticated: (state: AppState) => state.isAuthenticated,
  user: (state: AppState) => state.user,
  mapPosition: (state: AppState) => state.mapPosition,
  ownedParcels: (state: AppState) => state.ownedParcels,
  galleryParcels: (state: AppState) => state.galleryParcels,
  orders: (state: AppState) => state.orders,
  claimingParcelId: (state: AppState) => state.claimingParcelId,
};

// ============================================================================
// Actions (convenience methods for common state changes)
// ============================================================================

export const actions = {
  // Navigation
  navigateTo(view: AppState["currentView"]): void {
    store.setState({ currentView: view });
  },

  // Auth
  login(user: User): void {
    store.setState({ isAuthenticated: true, user });
  },

  logout(): void {
    store.setState({ isAuthenticated: false, user: null });
  },

  // Map
  setMapPosition(position: MapPosition): void {
    store.setState({ mapPosition: position });
  },

  // Parcels
  setParcels(parcels: Parcel[]): void {
    store.setState({ ownedParcels: parcels });
  },

  addParcel(parcel: Parcel): void {
    const current = store.getState().ownedParcels;
    store.setState({ ownedParcels: [...current, parcel] });
  },

  removeParcel(id: string): void {
    const current = store.getState().ownedParcels;
    store.setState({ ownedParcels: current.filter((p) => p.id !== id) });
  },

  updateParcel(id: string, updates: Partial<Parcel>): void {
    const current = store.getState().ownedParcels;
    store.setState({
      ownedParcels: current.map((p) =>
        p.id === id ? { ...p, ...updates } : p
      ),
    });
  },

  // Gallery
  setGalleryParcels(parcels: Parcel[]): void {
    store.setState({ galleryParcels: parcels });
  },

  addClaimedParcel(parcel: Parcel): void {
    const current = store.getState().galleryParcels;
    store.setState({ galleryParcels: [parcel, ...current] });
  },

  // Orders
  setOrders(orders: Order[]): void {
    store.setState({ orders });
  },

  // Claim flow
  setClaimingParcelId(id: string | null): void {
    store.setState({ claimingParcelId: id });
  },
};
